import "./components/NavbarMenuDropdown.css";
import "./Header.css";

import React, { type FC, useState } from "react";

import type { DetailedMenu } from "~/core";
import { Logo } from "~/icons";
import type { PageLink } from "~/root";
import { Accordion, useUiKitComponents } from "~/shared/ui-kit";

import { HeaderAuthButtons } from "./components/HeaderAuthButtons";
import { LocaleSelect } from "./components/LocaleSelect";
import { NavbarItem } from "./components/NavbarItem";
import { NavbarItemMobile } from "./components/NavbarItemMobile";

export type Option = {
  id: number;
  key: string;
  lang: LanguageCode;
  icon: string;
};

export type HeaderProps = {
  playNowLink: string;
  featureFlags: {
    isNavigationEnabled: boolean;
    isLanguageSelectorEnabled: boolean;
    isCreateButtonEnabled: boolean;
  };
  analytics: {
    clickHeaderMain: (targetUrl: string) => void;
    clickHeaderSub: (targetUrl: string) => void;
    loginHeader: () => void;
    createAccountHeader: () => void;
  };
  menuData?: DetailedMenu;
  language: LanguageCode;
  buildLink: (page: PageLink) => string;
};

export type MenuItemType = NonNullable<
  ExtractArrayItemType<NonNullable<DetailedMenu["items"]>>
>["menu_item_id"];

function hasMenuItemId(
  item: ExtractArrayItemType<NonNullable<DetailedMenu["items"]>>,
): item is ExtractArrayItemType<NonNullable<DetailedMenu["items"]>> & {
  menu_item_id: NonNullable<MenuItemType>;
} {
  return item?.menu_item_id !== undefined && item.menu_item_id !== null;
}

export const HeaderV2: FC<HeaderProps> = ({
  playNowLink,
  featureFlags,
  analytics: {
    clickHeaderMain,
    clickHeaderSub,
    createAccountHeader,
    loginHeader,
  },
  menuData,
  language,
  buildLink,
}) => {
  const [isBurgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const onBurgerMenuClick = (): void => setBurgerMenuOpen(!isBurgerMenuOpen);
  const [currentPage, setCurrentPage] = useState<string>("");
  const { Link } = useUiKitComponents();

  const onNavlinkClick = (url: string, id: string): void => {
    clickHeaderMain(url);
    setCurrentPage(id);
  };

  const onRouteClick = (): void => setBurgerMenuOpen(false);

  const listOfLang: Array<Option> = [
    {
      id: 1,
      key: "EN",
      lang: "en",
      icon: "/en-flag.svg",
    },
    {
      id: 2,
      key: "ZH",
      lang: "zh",
      icon: "/ch-flag.svg",
    },
  ];

  return (
    <header>
      <nav className="navbar">
        <Link onClick={onRouteClick} className="navbar-logo" href={"/"}>
          <Logo viewBox="0 0 189 49" />
        </Link>
        <div className={`navbar-left ${isBurgerMenuOpen && "opened"}`}>
          <>
            <Accordion>
              {featureFlags.isNavigationEnabled &&
                menuData?.items?.filter(hasMenuItemId).map((item) => (
                  <div
                    key={item.menu_item_id.id}
                    className="navbar-item-wrapper"
                  >
                    <NavbarItem
                      {...item.menu_item_id}
                      currentNavlink={currentPage}
                      onNavlinkClick={onNavlinkClick}
                      clickHeaderSub={clickHeaderSub}
                      buildLink={buildLink}
                    />
                    <NavbarItemMobile
                      {...item.menu_item_id}
                      onRouteClick={onRouteClick}
                      clickHeaderMain={clickHeaderMain}
                      clickHeaderSub={clickHeaderSub}
                      lang={language}
                      buildLink={buildLink}
                    />
                  </div>
                ))}
            </Accordion>

            {featureFlags.isLanguageSelectorEnabled && (
              <div className="navbar-item-wrapper">
                <div className="navbar-select-mobile">
                  <div className="navbar-item-mobile-wrapper locale-switch-wrapper">
                    <LocaleSelect languageList={listOfLang} locale={language} />
                  </div>
                </div>
              </div>
            )}
            {featureFlags.isCreateButtonEnabled && (
              <div className="navbar-item-wrapper">
                <div className="navbar-select-mobile">
                  <HeaderAuthButtons
                    featureFlags={featureFlags}
                    playNowLink={playNowLink}
                    burgerButton={true}
                    createAccountHeader={createAccountHeader}
                    loginHeader={loginHeader}
                  />
                </div>
              </div>
            )}
          </>
        </div>
        <div className="navbar-spacer" />
        {featureFlags.isLanguageSelectorEnabled && (
          <div className="navbar-item locale-switch locale-switch-desktop">
            <LocaleSelect languageList={listOfLang} locale={language} />
          </div>
        )}
        <div>
          <HeaderAuthButtons
            featureFlags={featureFlags}
            playNowLink={playNowLink}
            createAccountHeader={createAccountHeader}
            loginHeader={loginHeader}
          />
        </div>
        {featureFlags.isNavigationEnabled && (
          <div
            onClick={onBurgerMenuClick}
            className={`menu-button ${isBurgerMenuOpen && "opened"}`}
          >
            <span />
            <span />
            <span />
            <span />
          </div>
        )}
      </nav>
    </header>
  );
};
